import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { sanitizeFileName } from "utils/sanitizeFileName";
import { downloadWorkbook } from "../../ExportExcelButtonV3/utils";
import { buildRoomExportPositions } from "../../utils/buildRoomExportPositions";
import { buildRoomsWorkbook } from "../../utils/buildRoomsWorkbook";
import { countElementsByRoom } from "../../utils/rooms";
import type { BOQExtractionPosition } from "../../utils/types";
import type { UseExportRoomsExcel } from "./types";

const EMPTY_BOQ_POSITIONS: BOQExtractionPosition[] = [];

export const useExportRoomsExcel: UseExportRoomsExcel = ({
  selectedPlan,
  rooms,
  groupIds,
  byGroupId,
  boqPositions,
}) => {
  const { t } = useTranslation();

  const handleExportRoomsExcel = useCallback(async () => {
    if (!selectedPlan) return;

    const safeName = sanitizeFileName({
      rawName: selectedPlan.label ?? "floor-plan",
      fallback: "floor-plan",
    });

    const roomPositions = buildRoomExportPositions({
      roomElementCounts: countElementsByRoom({ rooms, groupIds, byGroupId }),
      groupMeta: selectedPlan.groupMeta,
      boqPositions: boqPositions ?? EMPTY_BOQ_POSITIONS,
    });

    const workbook = buildRoomsWorkbook({
      planName: selectedPlan.label,
      roomPositions,
      labels: {
        sheetName: t("smartSurveyor.rooms.excel.sheetName"),
        note: t("smartSurveyor.rooms.exportOverlapNote"),
        noRooms: t("smartSurveyor.rooms.excel.noRooms"),
        position: t("smartSurveyor.exportExcel.countListHeaderPosition"),
        description: t("smartSurveyor.exportExcel.countListHeaderDescription"),
        detectedType: t(
          "smartSurveyor.exportExcel.countListHeaderDetectedType",
        ),
        count: t("smartSurveyor.rooms.excel.count"),
        total: t("smartSurveyor.exportExcel.countListHeaderCountedTotal"),
      },
      formatRoomTotal: (count) =>
        t("smartSurveyor.exportExcel.legendElementsCount", { count }),
    });

    await downloadWorkbook(workbook, `${safeName}_rooms.xlsx`);
    trackEvent(ANALYTICS_EVENTS.EXCEL_EXPORTED, {
      exportVariant: "rooms",
      roomCount: rooms.length,
    });
  }, [t, selectedPlan, rooms, groupIds, byGroupId, boqPositions]);

  return { handleExportRoomsExcel };
};
